// src/pages/Role.js
import React, { useEffect, useState, useContext } from 'react';
import {
  Button,
  Container,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Typography,
  Box,
  Snackbar,
  Alert,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox
} from '@mui/material';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { TextField } from '../components/Input';
import { request } from '../utils/request';
import { toast } from '../utils/toast';
import { ColorModeContext } from '../index';

export const Role = () => {
  const { mode } = useContext(ColorModeContext);

  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Delete dialog / snackbar state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingRole, setDeletingRole] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // three-dot menu state for per-row actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };
  const onMenuEdit = (record) => {
    handleMenuClose();
    openDialog('edit', record);
  };
  const onMenuDelete = (record) => {
    handleMenuClose();
    handleDeleteClick(record);
  };

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  const validationSchema = Yup.object().shape({
    role: Yup.string().required('Role is required.'),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { role: '' },
  });
  const { handleSubmit, reset, setValue } = methods;

  // Fetch roles
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await request('/role-master/list', { method: 'GET' });
      if (res?.success) setRoles(res.data || []);
      else {
        console.warn('fetchRoles response error:', res);
        toast('Error fetching roles', { variant: 'error' });
      }
    } catch (err) {
      console.error('fetchRoles error:', err);
      toast('Error fetching roles', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (selectedRecord) setValue('role', selectedRecord.role || '');
    else reset({ role: '' });
  }, [selectedRecord, setValue, reset]);

  const openDialog = (action, record) => {
    setCurrentAction(action);
    setSelectedRecord(record);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setCurrentAction(undefined);
    setSelectedRecord(undefined);
    reset({ role: '' });
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const isEdit = currentAction === 'edit';
      const endpoint = isEdit ? '/role-master/update' : '/role-master/create';
      const method = isEdit ? 'PUT' : 'POST';
      await request(endpoint, {
        method,
        params: isEdit ? selectedRecord._id : null,
        body: data,
      });
      toast(`Role ${isEdit ? 'updated' : 'created'} successfully`, { variant: 'success' });
      await fetchRoles();
      closeDialog();
    } catch (err) {
      console.error('onSubmit error:', err);
      toast('Error saving role', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Single delete
  const handleDeleteClick = (record) => {
    setDeletingRole(record);
    setDeleteOpen(true);
  };
  const handleCancelDelete = () => {
    setDeletingRole(null);
    setDeleteOpen(false);
    setDeleting(false);
  };
  const handleConfirmDelete = async () => {
    if (!deletingRole || !deletingRole._id) return;
    setDeleting(true);
    try {
      await request('/role-master/delete', { method: 'DELETE', params: deletingRole._id });
      toast('Role deleted', { variant: 'success' });
      showSnack(`Role "${deletingRole.role}" successfully deleted.`, 'success');
      await fetchRoles();
      handleCancelDelete();
    } catch (err) {
      console.error('onDelete error:', err);
      toast('Error deleting role', { variant: 'error' });
      showSnack('Error deleting role', 'error');
      setDeleting(false);
    }
  };

  // -----------------------
  // Checkbox + selection logic
  // -----------------------
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const isAllSelected = roles.length > 0 && selectedIds.length === roles.length;
  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedIds([]);
    else setSelectedIds(roles.map(r => r._id));
  };

  const handleEditSelected = () => {
    if (selectedIds.length !== 1) return;
    const role = roles.find(r => r._id === selectedIds[0]);
    if (role) openDialog('edit', role);
    setSelectedIds([]);
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => request('/role-master/delete', { method: 'DELETE', params: id })));
      showSnack(`${selectedIds.length} role(s) deleted.`, 'success');
      setSelectedIds([]);
      await fetchRoles();
      handleCancelBulkDelete();
    } catch (err) {
      console.error('Bulk delete error', err);
      showSnack('Failed to delete selected roles.', 'error');
      setBulkDeleting(false);
    }
  };
  // -----------------------

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  return (
    <Box p={2}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Link to="/home" style={{ textDecoration: "none", color: mode === "dark" ? "#fff" : "#2d9cdb" }}
              onMouseEnter={(e) => e.currentTarget.style.color = "#0b1d51"}
              onMouseLeave={(e) => e.currentTarget.style.color = mode === "dark" ? "#fff" : "#2d9cdb"}>
              Dashboard
            </Link>
            <Typography>/</Typography>
            <Typography color={mode === 'dark' ? '#fff' : '#333'}>Role Master</Typography>
          </Stack>
        </Box>
        <Button size="small" variant="contained" onClick={() => openDialog('new')}
          sx={{ backgroundColor: '#2d9cdb', px: 2, py: 0.8, '&:hover': { backgroundColor: '#0b1d51', color: '#fff' } }}>
          Add Role
        </Button>
      </Stack>

      {/* Toolbar for selected roles */}
      {selectedIds.length > 0 && (
        <Box mb={1} display="flex" alignItems="center" gap={1}>
          <Typography>{selectedIds.length} selected</Typography>
          <Button size="small" variant="outlined" onClick={handleEditSelected} disabled={selectedIds.length !== 1}>Edit</Button>
          <Button size="small" variant="contained" onClick={handleDeleteSelected} sx={{ backgroundColor: '#dc3545', color: '#fff', '&:hover': { backgroundColor: '#b71c1c' } }}>
            Delete
          </Button>
          <Button size="small" onClick={() => setSelectedIds([])}>Clear</Button>
        </Box>
      )}

      {/* Table */}
      <Paper elevation={1}>
        <Box component="table" width="100%" sx={{ borderCollapse: 'collapse' }}>
          <Box component="thead" sx={{ backgroundColor: "#f5f7fb" }}>
            <Box component="tr">
              <Box component="th" p={1}>
                <Checkbox size="small" checked={isAllSelected} indeterminate={selectedIds.length > 0 && !isAllSelected} onChange={toggleSelectAll} />
              </Box>
              <Box component="th" p={1} textAlign="left" color={mode === 'dark' ? '#fff' : '#333'}>#</Box>
              <Box component="th" p={1} textAlign="left" color={mode === 'dark' ? '#fff' : '#333'}>Role</Box>
              <Box component="th" p={1} textAlign="right" color={mode === 'dark' ? '#fff' : '#333'}>Actions</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {roles.map((r, i) => (
              <Box component="tr" key={r._id} sx={{ '&:nth-of-type(even)': { backgroundColor: mode === 'dark' ? '#1e2538' : '#fafafa' } }}>
                <Box component="td" p={1}>
                  <Checkbox size="small" checked={selectedIds.includes(r._id)} onChange={() => toggleSelect(r._id)} />
                </Box>
                <Box component="td" p={1}>{i+1}</Box>
                <Box component="td" p={1}>{r.role}</Box>
                <Box component="td" p={1} textAlign="right">
                  <IconButton size="small" aria-controls={menuRowId===r._id ? `row-menu-${r._id}` : undefined} aria-haspopup="true" onClick={(e)=>handleMenuOpen(e,r._id)} sx={{ color: mode==='dark'?'#fff':'#333' }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu
                    id={`row-menu-${r._id}`}
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl) && menuRowId===r._id}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical:'bottom', horizontal:'right' }}
                    transformOrigin={{ vertical:'top', horizontal:'right' }}
                  >
                    <MenuItem onClick={()=>onMenuEdit(r)}>Edit</MenuItem>
                    <MenuItem onClick={()=>onMenuDelete(r)}>Delete</MenuItem>
                  </Menu>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Bulk delete confirmation */}
      <Dialog open={bulkDeleteOpen} onClose={handleCancelBulkDelete}>
        <DialogTitle>Delete Selected Roles</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete {selectedIds.length} role(s)?</Typography>
          <Box sx={{ mt:1 }}>
            {selectedIds.map(id => {
              const r = roles.find(role => role._id===id);
              return <Typography key={id}>• {r ? r.role : id}</Typography>
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelBulkDelete} disabled={bulkDeleting}>Cancel</Button>
          <Button onClick={handleConfirmBulkDelete} disabled={bulkDeleting} sx={{ backgroundColor:'#dc3545', '&:hover':{backgroundColor:'#b71c1c'}, color:'#fff' }}>
            {bulkDeleting ? 'Deleting…' : `Yes, Delete (${selectedIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -----------------------------
          ADDED: Add / Edit Role dialog (was missing)
         ----------------------------- */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="xs">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>{currentAction === 'edit' ? 'Edit Role' : 'Add Role'}</DialogTitle>
            <DialogContent dividers>
              {/* Using your project's TextField component which should integrate with react-hook-form */}
              <Box sx={{ mt: 1 }}>
                <TextField name="role" label="Role" />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog} disabled={loading}>Cancel</Button>
              <Button type="submit" disabled={loading} variant="contained" sx={{ backgroundColor: '#2d9cdb' }}>
                {loading ? (currentAction === 'edit' ? 'Updating…' : 'Saving…') : (currentAction === 'edit' ? 'Update' : 'Save')}
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>

      {/* -----------------------------
          ADDED: Single-role Delete confirmation dialog (was missing)
         ----------------------------- */}
      <Dialog open={deleteOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this role?</Typography>
          {deletingRole && <Typography sx={{ mt: 1 }}>• {deletingRole.role}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>Cancel</Button>
          <Button onClick={handleConfirmDelete} disabled={deleting} sx={{ backgroundColor: '#dc3545', color:'#fff' }}>
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackOpen} autoHideDuration={3000} onClose={(e,r)=>{if(r!=='clickaway') setSnackOpen(false)}} anchorOrigin={{ vertical:'bottom', horizontal:'right' }}>
        <Alert onClose={(e,r)=>{if(r!=='clickaway') setSnackOpen(false)}} severity={snackSeverity} sx={{ width:'100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};
